import Box from "@mui/material/Box";
import { Theme } from "../data/style/Theme";
import { saveWorkout } from "../api/workouts";
import { useNavigate } from "react-router-dom";
import * as routes from "../data/links/routes";
import { useAuth } from "../context/AuthContext";
import { errorTexts } from "../data/assets/texts";
import Loader from "../components/Display/Loader";
import useScreenSize from "../context/ScreenContext";
import MainCard from "../components/Layouts/MainCard";
import { CardTitle } from "../components/theme/texts";
import { getWorkoutStructures } from "../api/helpers";
import { WORKOUT_TYPES_DATA } from "../data/assets/arrays";
import { useEffect, useState, lazy, Suspense } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNotifications } from "../context/NotificationContext";
import { PlusMinusButton } from "../components/buttons/PlusMinusButton";
import {
  scrollBar,
  MainText,
  AllCenteredColumn,
  AllCenteredRow,
  SpacedRow,
  MainTitle,
  FormButton,
  CenteredRow,
  SecondaryTitle,
} from "../data/style/Style";

const Stack = lazy(() => import("@mui/material/Stack"));
const ExercisePanel = lazy(() => import("../components/ExercisePanel"));
const WorkoutPreview = lazy(() => import("../components/WorkoutPreview"));

const TIMECAP_PRESETS = [7, 10, 12, 15, 20, 25, 30];

const CreateWorkout = () => {
  const { size, mobileDevice } = useScreenSize();
  const [timecap, setTimecap] = useState(0);
  const [every, setEvery] = useState(0);
  const [rounds, setRounds] = useState(0);
  const [rest, setRest] = useState(0);
  const { user } = useAuth();
  const [workoutType, setWorkoutType] = useState(1);
  const { enableNotification } = useNotifications();
  const [wodExercises, setWodExercises] = useState([]);
  var [width] = size;
  const navigate = useNavigate();
  const [structure, setStructure] = useState({
    id: 1,
    name: "Not selected",
    display: 0,
    exercise_number: 0,
    start_reps: 0,
    structure: 1,
    rep_step: 0,
    pattern: 0,
    total_reps: 0,
  });

  const { data: structures, isPending: structuresPending } = useQuery({
    queryKey: ["structures"],
    queryFn: ({ signal }) => getWorkoutStructures({ signal }),
    staleTime: 12 * 60 * 60 * 1000,
  });

  useEffect(() => {
    if (structure !== null && structure?.exercise_number !== 0) {
      setWodExercises((prev) => prev.slice(0, structure.exercise_number));
    }
  }, [structure?.id]);

  useEffect(() => {
    if (workoutType) {
      if (rest === 0) {
        setTimecap(every * rounds);
      } else {
        setTimecap((every + rest) * rounds);
      }
    }
  }, [rounds, every, rest, workoutType]);

  let addExerciseToWorkout = (exercise) => {
    if (
      structure !== null &&
      structure.exercise_number !== 0 &&
      wodExercises.length >= structure.exercise_number
    ) {
      enableNotification(errorTexts.exercise_number_error, "info");
      return;
    }
    setWodExercises((current) => [
      ...current,
      {
        id: exercise.id,
        name: exercise.name,
        gender_diff: exercise.gender_diff,
        category_id: exercise.category,
        reps: 1,
        step: 1,
        weight_id: exercise.category < 4 ? 47 : exercise.allowed_weights[0].id,
        allowed_weights: exercise.allowed_weights,
        unit:
          exercise.category === 3 && exercise.has_multiple_units === 1
            ? "cal"
            : (exercise.category === 5 || exercise.category === 4) &&
                exercise.has_multiple_units === 1
              ? "m"
              : "",
      },
    ]);
  };

  useEffect(() => {
    setStructure({
      id: 1,
      name: "Not selected",
      display: 0,
      exercise_number: 0,
      start_reps: 0,
      structure: 1,
      rep_step: 0,
      pattern: 0,
      total_reps: 0,
    });
  }, [workoutType]);

  useEffect(() => {
    if (workoutType === 4 && rest !== 0) {
      setWodExercises((prev) =>
        prev.map((item, index) =>
          index === wodExercises.length - 1
            ? { ...item, reps: 5000 }
            : item.reps === 5000
              ? { ...item, reps: 1 }
              : item,
        ),
      );
    }
  }, [wodExercises.length, rest, workoutType]);

  const { mutate } = useMutation({
    mutationFn: saveWorkout,
    onSuccess: (response) => {
      enableNotification(response.data.message, "success");
      setWodExercises([]);
      setEvery(0);
      setStructure({
        id: 1,
        name: "Not selected",
        display: 0,
        exercise_number: 0,
        start_reps: 0,
        structure: 1,
        rep_step: 0,
        pattern: 0,
        total_reps: 0,
      });
      setRounds(0);
      setRest(0);
      setTimecap(0);
    },
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }
      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }
      if (error.response.status >= 500) {
        enableNotification(error.response.data.message, "error");
        return;
      }
    },
  });

  let postWorkout = () => {
    if (wodExercises.length === 0) {
      enableNotification("No exercises added to the workout.", "info");
      return;
    }

    if (timecap === 0) {
      enableNotification("Please define the timecap", "info");
      return;
    }

    if (workoutType === 3 && timecap % wodExercises.length !== 0) {
      enableNotification("Wrong emom format.", "info");
      return;
    }

    if (workoutType === 4 && rest === 0 && timecap % every !== 0) {
      enableNotification("Wrong interval format.", "info");
      return;
    }

    if (structure?.id === 1) {
      if (wodExercises.find((item) => item.reps === 0)) {
        enableNotification("Some exercise reps are missing.", "info");
        return;
      }
    }

    mutate({
      workoutTypeId: workoutType,
      timecap,
      structureId: structure?.structure,
      every,
      rest,
      rounds,
      userId: user.id,
      wodExercises,
      created: 1,
    });
  };

  function handleRoundsChange(action) {
    switch (action) {
      case "+":
        setRounds((prev) => prev + 1);
        break;
      case "-":
        setRounds((prev) => Math.max(0, prev - 1));
        break;
      default:
        return;
    }
  }

  function handleEveryChange(action) {
    switch (action) {
      case "+":
        setEvery((prev) => prev + 1);
        break;
      case "-":
        setEvery((prev) => Math.max(0, prev - 1));
        break;
      default:
        return;
    }
  }

  function handleRestChange(action) {
    switch (action) {
      case "+":
        setRest((prev) => prev + 1);
        break;
      case "-":
        setRest((prev) => Math.max(0, prev - 1));
        break;
      default:
        return;
    }
  }
  return (
    <Stack
      height={"100%"}
      sx={scrollBar}
      overflow={"auto"}
      direction={"row"}
      flexWrap={"wrap"}
      gap={3}
      pr={1}
    >
      <Stack
        gap={2}
        justifyContent={"space-between"}
        flex={mobileDevice ? 1 : 2}
        minWidth={"270px"}
      >
        <Stack flex={1} minHeight={"27vh"} flexWrap={"wrap"}>
          <MainCard>
            <CardTitle subTitle={"Step 1"} mainTitle={"Workout Type"} />
            <SpacedRow gap={1} flexWrap={"wrap"}>
              {WORKOUT_TYPES_DATA.map((item) => (
                <AllCenteredColumn
                  key={item.id}
                  onClick={() => setWorkoutType(item.id)}
                  p={1}
                  sx={{
                    minWidth: "60px",
                    cursor: "pointer",
                    border: 1,
                    borderRadius: 3,
                    background:
                      item.id === workoutType &&
                      WORKOUT_TYPES_DATA.find((obj) => obj.id === item.id)
                        .color + "22",
                    borderColor:
                      workoutType === item.id
                        ? item.color
                        : Theme.palette.border,
                  }}
                >
                  <MainText>{item.icon}</MainText>
                  <MainText
                    sx={{
                      color:
                        workoutType === item.id && item.color + " !important",
                    }}
                  >
                    {item.name}
                  </MainText>
                </AllCenteredColumn>
              ))}
            </SpacedRow>
            {workoutType !== 0 && (
              <Stack
                direction={"row"}
                gap={2}
                mt={2}
                p={1}
                sx={{
                  border: 1,
                  borderRadius: 3,
                  borderColor: WORKOUT_TYPES_DATA.find(
                    (item) => item.id === workoutType,
                  ).color,
                  background:
                    WORKOUT_TYPES_DATA.find((item) => item.id === workoutType)
                      .color + "22",
                }}
              >
                <MainText>
                  {
                    WORKOUT_TYPES_DATA.find((item) => item.id === workoutType)
                      .icon
                  }
                </MainText>
                <MainText
                  sx={{
                    color:
                      WORKOUT_TYPES_DATA.find((item) => item.id === workoutType)
                        .color + " !important",
                  }}
                >
                  {
                    WORKOUT_TYPES_DATA.find((item) => item.id === workoutType)
                      .description
                  }
                </MainText>
              </Stack>
            )}
          </MainCard>
        </Stack>
        <Stack width={"100%"} minHeight={"27vh"}>
          <MainCard>
            <Stack gap={1}>
              <CardTitle
                subTitle={"Step 2"}
                mainTitle={workoutType !== 4 ? "Timecap" : "Interval Config"}
              />
              {workoutType !== 4 ? (
                <>
                  <AllCenteredRow gap={3}>
                    <button
                      style={{
                        cursor: "pointer",
                        background: Theme.palette.card,
                        border: `1px solid ${Theme.palette.border}`,
                        borderRadius: "12px",
                        width: 40,
                        height: 40,
                        color: Theme.palette.text,
                        fontSize: "2rem",
                      }}
                      onClick={() =>
                        setTimecap((value) => Math.max(0, value - 1))
                      }
                    >
                      -
                    </button>
                    <AllCenteredColumn width={"70px"} height={"70px"}>
                      <MainTitle
                        fontSize={"2.3rem"}
                        sx={{ color: Theme.palette.orange + " !important" }}
                      >
                        {timecap}
                      </MainTitle>
                      <MainText
                        fontSize={"0.8rem"}
                        sx={{ color: Theme.palette.muted + " !important" }}
                      >
                        minutes
                      </MainText>
                    </AllCenteredColumn>
                    <button
                      style={{
                        cursor: "pointer",
                        background: Theme.palette.card,
                        border: `1px solid ${Theme.palette.border}`,
                        borderRadius: "12px",
                        width: 40,
                        height: 40,
                        borderColor: Theme.palette.border,
                        color: Theme.palette.text,
                        fontSize: "2rem",
                      }}
                      onClick={() =>
                        setTimecap((value) => Math.min(60, value + 1))
                      }
                    >
                      +
                    </button>
                  </AllCenteredRow>
                  <AllCenteredRow gap={1}>
                    {TIMECAP_PRESETS.map((item) => (
                      <AllCenteredRow
                        key={item}
                        gap={2}
                        onClick={() => setTimecap(item)}
                        sx={{
                          cursor: "pointer",
                          width: 35,
                          height: 35,
                          borderRadius: 3,
                          background:
                            timecap === item
                              ? "rgba(249,115,22,0.12)"
                              : Theme.palette.card,
                          border: 1,
                          borderColor:
                            timecap === item
                              ? "rgba(249,115,22,0.4)"
                              : Theme.palette.border,

                          "&:hover": {
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <MainText
                          sx={{
                            color: timecap === item && Theme.palette.orange,
                          }}
                          fontWeight={"bold"}
                        >
                          {item}
                        </MainText>
                      </AllCenteredRow>
                    ))}
                  </AllCenteredRow>
                </>
              ) : (
                <>
                  <AllCenteredRow mt={-1} gap={2}>
                    <IntervalElement
                      element={"Rounds"}
                      handleChange={handleRoundsChange}
                      value={rounds}
                      color={Theme.palette.orange}
                    />
                    <IntervalElement
                      element={"Work (Min)"}
                      handleChange={handleEveryChange}
                      value={every}
                      color={Theme.palette.up}
                    />
                    <IntervalElement
                      element={"Rest (Min)"}
                      handleChange={handleRestChange}
                      value={rest}
                      color={Theme.palette.down}
                    />
                  </AllCenteredRow>
                  {rounds !== 0 && (
                    <Box
                      mt={1}
                      sx={{
                        border: 1,
                        borderColor: Theme.palette.orange,
                        background: Theme.palette.orange + "22",
                        padding: 1,
                        borderRadius: 3,
                      }}
                    >
                      <MainText>
                        {rest === 0 ? (
                          <>
                            Every {every} for {timecap} minutes
                          </>
                        ) : (
                          <>
                            {rounds} {rounds === 1 ? "round" : "rounds"}:{" "}
                            {every} {every === 1 ? "minute" : "minutes"} on /{" "}
                            {rest} {rest === 1 ? "minute" : "minutes"} off
                          </>
                        )}
                      </MainText>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </MainCard>
        </Stack>
        <Stack width={"100%"} minHeight={"25vh"}>
          <MainCard>
            <CardTitle subTitle={"Step 3"} mainTitle={"Structure"} />
            {structuresPending ? (
              <Loader minHeight={"100px"} />
            ) : (
              <CenteredRow
                flexWrap={"wrap"}
                gap={1}
                minHeight={"100px"}
                height={"16vh"}
                sx={scrollBar}
                overflow={"auto"}
              >
                {structures.map((item, index) => (
                  <Structure
                    key={index}
                    onClick={() => {
                      if (workoutType !== 1) return;
                      setStructure(item);
                    }}
                    workoutType={workoutType}
                    structure={structure}
                    item={item}
                  />
                ))}
              </CenteredRow>
            )}
          </MainCard>
        </Stack>
      </Stack>
      <Stack
        height={mobileDevice || width < 750 ? "50vh" : "100%"}
        minHeight={"300px"}
        overflow={"auto"}
        sx={scrollBar}
        flex={1}
        minWidth={"270px"}
      >
        <ExercisePanel
          addExerciseToWorkout={addExerciseToWorkout}
          step={workoutType === 1 ? 4 : 3}
        />
      </Stack>
      <Stack minWidth={"270px"} flex={mobileDevice ? 1 : 2} gap={2}>
        <Stack height={mobileDevice || width < 900 ? "100%" : "60%"}>
          <MainCard>
            <Stack gap={1}>
              <CardTitle
                subTitle={"Live Preview"}
                mainTitle={
                  workoutType !== 0
                    ? WORKOUT_TYPES_DATA.find((item) => item.id === workoutType)
                        .icon +
                      " " +
                      WORKOUT_TYPES_DATA.find((item) => item.id === workoutType)
                        .name
                    : null
                }
              />
              {structure?.display === 1 && (
                <MainText
                  fontWeight={700}
                  fontSize={"1.2rem"}
                  sx={{
                    color: Theme.palette.orange + " !important",
                    alignSelf: "center",
                  }}
                >
                  {structure?.name}
                </MainText>
              )}
              <Suspense>
                <WorkoutPreview
                  wodExercises={wodExercises}
                  setWodExercises={setWodExercises}
                  structure={structure}
                  workoutType={workoutType}
                  rest={rest}
                />
              </Suspense>
            </Stack>
          </MainCard>
        </Stack>
        <SpacedRow gap={3} pb={2}>
          <Box flex={1}>
            <FormButton
              onClick={() => setWodExercises([])}
              alt={true}
              small={true}
            >
              Clear
            </FormButton>
          </Box>
          <Box flex={1}>
            <FormButton onClick={postWorkout} small={true}>
              Save
            </FormButton>
          </Box>
        </SpacedRow>
      </Stack>
    </Stack>
  );
};
export default CreateWorkout;

const IntervalElement = ({ element, handleChange, value, color }) => {
  return (
    <Stack
      alignItems={"center"}
      sx={{
        background: Theme.palette.card,
        border: 1,
        borderColor: Theme.palette.border,
        borderRadius: 4,
        padding: 0.4,
      }}
    >
      <SecondaryTitle sx={{ color: color + " !important" }}>
        {element}
      </SecondaryTitle>
      <CenteredRow gap={1}>
        <PlusMinusButton
          action={"-"}
          onClick={() => handleChange("-")}
          disabled={value === 0}
          alt={true}
        />
        <Box minWidth={"20px"} sx={{ textAlign: "center" }}>
          <MainText
            fontWeight={700}
            fontSize={"1.8rem"}
            sx={{ color: color + " !important" }}
          >
            {value}
          </MainText>
        </Box>
        <PlusMinusButton
          action={"+"}
          onClick={() => handleChange("+")}
          disabled={false}
          alt={true}
        />
      </CenteredRow>
    </Stack>
  );
};

const Structure = ({ workoutType, structure, item }) => {
  return (
    <Box
      sx={{
        cursor: workoutType === 1 ? "pointer" : "not-allowed",
        borderRadius: 4,
        padding: 0.5,
        border: 1,
        borderColor:
          workoutType !== 1 ? Theme.palette.border : Theme.palette.orange,
        background:
          workoutType !== 1
            ? Theme.palette.muted
            : structure?.id === item.id
              ? Theme.palette.orange
              : null,
      }}
    >
      <MainText
        fontSize={"0.9rem"}
        sx={{
          color: workoutType !== 1 && Theme.palette.dim + " !important",
        }}
      >
        {item.name}
      </MainText>
    </Box>
  );
};
